import { useState } from "react";

import { useAuth } from "@/components/auth/auth-context";
import { HideMessageDialog } from "@/components/conversations/hide-message-dialog";
import { ChatMessageItem } from "@/components/shared/chat-message-item";
import type {
  ChatConversationMember,
  ChatMessage,
} from "@/components/shared/chat-message-types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Message, Reaction } from "@/lib/api/admin";
import { useResolveFileMessage } from "@/lib/api/messages";
import { useUser } from "@/lib/api/user";
import { hasPrivilegedAccessFromUser } from "@/lib/casl/privileged-access";
import { getSendbirdMessageSenderDisplayName } from "@/lib/redacted-display";
import { isMessageHidden } from "@/lib/utils/parse-message-data";
import { MoreVertical } from "lucide-react";

interface MessageItemProps {
  message: Message;
  showOnRightSide: boolean;
  isHighlighted?: boolean;
  conversationMembers?: ChatConversationMember[];
  conversationId?: string | null;
}

export function MessageItem({
  message,
  showOnRightSide,
  isHighlighted,
  conversationMembers = [],
  conversationId = null,
}: MessageItemProps) {
  const { user } = useAuth();
  const [showHideDialog, setShowHideDialog] = useState(false);
  const hasPrivilegedAccess = hasPrivilegedAccessFromUser(user);
  const isHidden = isMessageHidden(message.data);
  const isFileMessage = message.type === "FILE";

  const {
    signedUrl,
    transcription,
    isResolving,
    isFetching,
    requestTranscript,
  } = useResolveFileMessage(
    isFileMessage ? String(message.message_id) : null,
    message.req_id ?? null,
  );
  const signedMediaUrl = signedUrl ?? message.file?.url;

  const isMediaPending = isResolving && !signedMediaUrl;
  const senderLabel =
    getSendbirdMessageSenderDisplayName(message.user) ?? "Admin";

  const { data: senderUser } = useUser(message.user?.user_id ?? "");

  return (
    <>
      <ChatMessageItem
        message={normalizeAdminMessage(message, {
          signedMediaUrl,
          attachmentPending: isMediaPending,
          transcription,
          transcriptionPending: isFetching,
        })}
        layout={{
          alignment: showOnRightSide ? "right" : "left",
          messageStyle: showOnRightSide ? "outgoing" : "incoming",
          dimWhenHidden: true,
          isHighlighted,
        }}
        sender={{
          label: senderLabel,
          profileUrl: senderUser?.profile_picture_url,
        }}
        slots={{
          bubbleAccessories: (
            <>
              {message.reactions.length > 0 && (
                <Reactions reactions={message.reactions} />
              )}
              {hasPrivilegedAccess && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button className="flex min-h-10 min-w-10 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200">
                      <MoreVertical />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => setShowHideDialog(true)}
                      className="cursor-pointer"
                    >
                      {isHidden ? "Unhide message" : "Hide message"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          ),
        }}
        context={{
          surface: "admin",
          conversation: {
            id: conversationId,
            members: conversationMembers,
          },
        }}
        behavior={{
          onRequestTranscript: requestTranscript,
          onParentMessageClick: () => {
            const scrollTargetId =
              message.parent_message_id ??
              message.parent_message_info?.message_id;
            if (!scrollTargetId) return;
            document
              .querySelector(`[data-sb-message-id="${scrollTargetId}"]`)
              ?.scrollIntoView({ behavior: "smooth" });
          },
        }}
      />

      <HideMessageDialog
        open={showHideDialog}
        onClose={() => setShowHideDialog(false)}
        messageId={String(message.message_id)}
        isHidden={isHidden}
        onSuccess={() => {}}
      />
    </>
  );
}

function normalizeAdminMessage(
  message: Message,
  options: {
    signedMediaUrl?: string | null;
    attachmentPending?: boolean;
    transcription?: string | null;
    transcriptionPending?: boolean;
  },
): ChatMessage {
  return {
    id: message.message_id,
    kind:
      message.type === "FILE"
        ? "file"
        : message.type === "ADMM"
          ? "admin"
          : "message",
    text: message.message,
    data: message.data,
    customType: message.custom_type,
    createdAt: message.created_at,
    sender: message.user
      ? {
          id: message.user.user_id,
          nickname: message.user.nickname,
          displayName:
            getSendbirdMessageSenderDisplayName(message.user) ?? "Admin",
          profileUrl: message.user.profile_url,
        }
      : null,
    attachment:
      message.type === "FILE"
        ? {
            url: options.signedMediaUrl ?? message.file?.url,
            mimeType: message.file?.type,
          }
        : null,
    attachmentPending: options.attachmentPending,
    transcription: options.transcription,
    transcriptionPending: options.transcriptionPending,
    parentMessage: message.parent_message_info
      ? normalizeAdminMessage(message.parent_message_info, {})
      : null,
    translations: message.translations,
  };
}

function Reactions({ reactions }: { reactions: Reaction[] }) {
  return (
    <TooltipProvider>
      <div className="flex space-x-2">
        {reactions.map((reaction, index) => (
          <Tooltip key={index}>
            <TooltipTrigger asChild>
              <div className="group relative">
                <img src={reaction.emoji} alt="emoji" className="h-6 w-6" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <span>
                {reaction.users.map((entry) => entry.name).join(", ")}
              </span>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
